import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import {
  Figure,
  LoadMore,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
} from '@/pages/cvm/shared';
import { fetchCvmSessions } from '@/services/cvm';
import type { CvmSessionRow } from '@/services/types';

const PAGE_SIZE = 50;
const KINDS = ['JULGAMENTO', 'COLEGIADO', 'CRSFN'] as const;

/**
 * The session archive: three kinds of sitting, never pooled.
 *
 * A judgment session of the Colegiado (dated by the extrato), a Colegiado
 * meeting that decided settlement proposals, and a CRSFN session on appeal.
 * Each row counts what that sitting decided; the archive panel says how many
 * sessions carry a number and a date, because a session is the document that
 * records it and older documents record less.
 */
export function CvmSessionsPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [kind, setKind] = useState(seed.get('kind') ?? '');
  const filters = { kind: kind || null };
  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'sessions', filters],
    queryFn: ({ pageParam }) =>
      fetchCvmSessions({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:sessions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:sessions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:sessions.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {data ? (
        <section className="rounded-lg border bg-card p-4" data-archive>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('cvm:sessions.archive.title')}
          </h2>
          <Answer answer={data.archive} help={t('cvm:sessions.archive.help')}>
            {(a) => (
              <>
                <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Figure
                    label={t('cvm:sessions.archive.sessions')}
                    value={number.format(a.sessions)}
                  />
                  <Figure
                    label={t('cvm:sessions.archive.judgment')}
                    value={number.format(a.judgment_sessions)}
                  />
                  <Figure
                    label={t('cvm:sessions.archive.colegiado')}
                    value={number.format(a.colegiado_meetings)}
                  />
                  <Figure
                    label={t('cvm:sessions.archive.crsfn')}
                    value={number.format(a.crsfn_sessions)}
                  />
                </dl>
                <dl className="mt-2 grid grid-cols-3 gap-2">
                  <Figure
                    label={t('cvm:sessions.archive.numbered')}
                    value={number.format(a.with_number)}
                  />
                  <Figure
                    label={t('cvm:sessions.archive.dated')}
                    value={number.format(a.with_date)}
                  />
                  <Figure
                    label={t('cvm:sessions.archive.decisions')}
                    value={number.format(a.decisions)}
                  />
                </dl>
              </>
            )}
          </Answer>
        </section>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="space-y-1">
          <label htmlFor="cvm-session-kind" className="block text-xs font-medium text-foreground">
            {t('cvm:sessions.filters.kind')}
          </label>
          <select
            id="cvm-session-kind"
            value={kind}
            onChange={(event) => setKind(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('cvm:sessions.filters.any')}</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`cvm:sessionKinds.${k}`)}
              </option>
            ))}
          </select>
        </div>
      </form>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : data.total === 0 ? (
        <EmptyState hint={t('cvm:list.empty')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-total={data.total}
            data-stale={isPlaceholderData ? 'true' : 'false'}
          >
            {t('cvm:list.showing', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className={TABLE_REGION}
            role="region"
            aria-label={t('cvm:sessions.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:sessions.columns.kind')}</th>
                  <th className={TH}>{t('cvm:sessions.columns.date')}</th>
                  <th className={TH}>{t('cvm:sessions.columns.session')}</th>
                  <th className={TH_NUM}>{t('cvm:sessions.columns.decisions')}</th>
                  <th className={TH_NUM}>{t('cvm:sessions.columns.cases')}</th>
                  <th className={TH_NUM}>{t('cvm:sessions.columns.withSanctions')}</th>
                  <th className={TH_NUM}>{t('cvm:sessions.columns.withDissent')}</th>
                  <th className={TH_NUM}>{t('cvm:sessions.columns.settlements')}</th>
                  <th className={TH_NUM}>{t('cvm:sessions.columns.appeals')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => (
                  <SessionRow
                    key={`${row.session_kind}-${row.session_date}-${row.session_number}-${index}`}
                    row={row}
                  />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <LoadMore
              hasNextPage={Boolean(hasNextPage)}
              isFetchingNextPage={isFetchingNextPage}
              loadMore={loadMore}
              total={data.total}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}

      <FieldGuide i18nKey="cvm:sessions.guide" />
    </div>
  );
}

function SessionRow({ row }: { row: CvmSessionRow }) {
  const { t, i18n } = useTranslation(['cvm']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <tr className="border-t hover:bg-muted/30">
      <td className={TD_MUTED}>
        {t(`cvm:sessionKinds.${row.session_kind}`, {
          defaultValue: row.session_kind,
        })}
      </td>
      <td className={TD}>
        <span className="tabular-nums">{row.session_date ?? '—'}</span>
      </td>
      <td className={TD}>
        {row.session_number
          ? t('cvm:sessions.numbered', { number: row.session_number })
          : (row.session_label ?? t('cvm:sessions.unnumbered'))}
      </td>
      <td className={TD_NUM}>{number.format(row.decisions)}</td>
      <td className={TD_NUM}>{number.format(row.cases)}</td>
      <td className={TD_NUM}>{number.format(row.with_sanctions)}</td>
      <td className={TD_NUM}>{number.format(row.with_dissent)}</td>
      <td className={TD_NUM}>{number.format(row.settlements)}</td>
      <td className={TD_NUM}>{number.format(row.appeals)}</td>
    </tr>
  );
}
